import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLang } from '../context/LangContext';
import { countries } from '../data/countries';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiX, FiGlobe } from 'react-icons/fi';
import { cn } from '../utils/cn';

export default function CountryChangeModal({
  isOpen,
  onClose,
  onConfirm,
  replacingCountry,
  excludedCodes = [],
}) {
  const { t } = useLang();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const replacingName = countries.find((c) => c.code === replacingCountry)?.name ?? replacingCountry;

  const filtered = countries.filter((c) => {
    if (excludedCodes.includes(c.code)) return false;
    if (c.code === replacingCountry) return false;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
  });

  const handleConfirm = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      const ok = await onConfirm(selected.code);
      if (ok !== false) {
        setSearch('');
        setSelected(null);
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
      >
        <div className="p-6 border-b border-slate-800 flex justify-between items-center">
          <h2 className="text-xl font-black uppercase italic tracking-tighter flex items-center gap-2">
            <SafeIcon icon={FiGlobe} className="text-lime-400" />
            {t('profile_change_country_title')}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <SafeIcon icon={FiX} size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-400">
            {t('profile_change_country_desc', replacingName)}
          </p>

          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder={t('search_countries')}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg py-2 pl-10 pr-4 text-sm outline-none focus:border-lime-400 transition-colors"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="max-h-56 overflow-y-auto custom-scrollbar border border-slate-800 rounded-lg">
            {filtered.length === 0 ? (
              <p className="p-4 text-sm text-slate-500 text-center">{t('profile_no_countries')}</p>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => setSelected(c)}
                  className={cn(
                    'w-full text-left p-3 text-sm transition-colors flex items-center justify-between',
                    selected?.code === c.code
                      ? 'bg-lime-400/10 text-lime-400'
                      : 'hover:bg-slate-800 text-slate-400'
                  )}
                >
                  <span>{c.name}</span>
                  <span className="text-[10px] font-bold uppercase text-slate-600">{c.code}</span>
                </button>
              ))
            )}
          </div>

          <button
            type="button"
            disabled={!selected || submitting}
            onClick={handleConfirm}
            className="w-full py-4 bg-lime-400 disabled:bg-slate-800 disabled:text-slate-600 text-slate-950 font-black uppercase tracking-widest rounded-xl transition-all hover:scale-[1.02]"
          >
            {submitting ? t('loading') : t('profile_confirm_change')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
